import React, { useState, useEffect } from "react";
import {
    Box, Typography, Grid, FormControl, InputLabel, Select, MenuItem,
    TextField, Button, Modal, Backdrop, FormControlLabel, Checkbox,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import { useDollar } from "../../../DollarPrice";
import { getAuthData } from "../../utils/auth";

const BANCO_RECEPTOR = "Banco Banesco";
const NUMERO_CUENTA = "0134-0319-82-3191111990";
const TELEFONO_PAGO_MOVIL = "0414-4282678";
const RIF = "J-408124637";
const apiUrl = process.env.REACT_APP_API_URL;

const ReportePagosCuotasModal = ({ open, onClose, cuota, contrato, onPagoRegistrado }) => {
    const { dollarPrice } = useDollar();

    const [metodoPago, setMetodoPago] = useState("USD");
    const [esPagoEfectivoDivisas, setEsPagoEfectivoDivisas] = useState(false);
    const [montoPagar, setMontoPagar] = useState(0);
    const [descuentoAplicado, setDescuentoAplicado] = useState(0);

    const [pagoData, setPagoData] = useState({
        bancoEmisorVes: "",
        referenciaVes: "",
        comprobante: null,
    });

    const authData = getAuthData();
    const token = authData?.token;
    const usuario_id = authData?.userId;
    const rol = authData?.rol;
    const vendedor_id = authData?.vendedorId;
    const supervisor_id = authData?.supervisorId;

    const banks = [
        { value: "", label: "Seleccione un Banco" },
        { value: "Banesco", label: "Banco Banesco Banco Universal, C.A." },
        { value: "100% Banco", label: "100% Banco Banco Universal, C.A." },
        { value: "Banca Amiga", label: "Banca Amiga, Banco Microfinanciero" },
        { value: "Bancaribe", label: "Bancaribe, C.A. Banco Universal" },
        { value: "Banco Activo", label: "Banco Activo" },
        { value: "Banco Bicentenario", label: "Banco Bicentenario" },
        { value: "Banco Caroní", label: "Banco Caroní, C.A. Banco Universal" },
        { value: "Banco de la FANB", label: "Banco de la FANB" },
        { value: "Banco de Venezuela", label: "Banco de Venezuela" },
        { value: "Banco del Tesoro", label: "Banco del Tesoro" },
        { value: "Banco Exterior", label: "Banco Exterior, C.A. Banco Universal" },
        { value: "Banco Mercantil", label: "Banco Mercantil" },
        { value: "BNC", label: "Banco Nacional de Crédito (BNC)" },
        { value: "Banco Provincial", label: "Banco Provincial" },
        { value: "Banplus", label: "Banplus Banco Universal, C.A." },
    ];

    // Lógica de descuento (Tu lógica original)
    useEffect(() => {
        if (metodoPago === "USD" && esPagoEfectivoDivisas) {
            const desc = Number(montoPagar) * 0.20;
            setDescuentoAplicado(desc);
        } else {
            setDescuentoAplicado(0);
        }
    }, [esPagoEfectivoDivisas, metodoPago, montoPagar]);

    // Sincronización con la cuota seleccionada
    useEffect(() => {
        if (cuota && open) {
            setMontoPagar(cuota.monto_por_cuota || 0);
            setMetodoPago(cuota.metodo_pago || "USD");

            if (cuota.tipo_plan === "producto") {
                setEsPagoEfectivoDivisas(true);
            }
        }
    }, [cuota, open]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPagoData({ ...pagoData, comprobante: file });
        }
    };

    const isFormValid = () => {
        if (metodoPago === "VES") {
            return pagoData.bancoEmisorVes !== "" && pagoData.referenciaVes !== "" && montoPagar > 0;
        }
        return montoPagar > 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            let montoAbonadoValue = 0;
            if (metodoPago === "VES") {
                montoAbonadoValue = Number(montoPagar) / Number(dollarPrice);
            } else {
                montoAbonadoValue = Number(montoPagar) + descuentoAplicado;
            }

            const formData = new FormData();
            if (rol === "administrador") {
                formData.append("vendedor_id", usuario_id);
            } else {
                formData.append("vendedor_id", vendedor_id);
            }
            formData.append("supervisor_id", supervisor_id);
            formData.append("cuota_id", cuota.id);
            formData.append("contrato_id", contrato.id);
            formData.append("numero_contrato", contrato.numero_contrato);
            formData.append("modalidad_pago", "financiado");
            formData.append("monto_abonado_usd", montoAbonadoValue.toFixed(2));
            formData.append("nro_cuota", cuota.no_cuota);
            formData.append("monto_pagado", montoPagar);
            formData.append("descuento", descuentoAplicado.toFixed(2));
            formData.append("metodo_pago", metodoPago);
            formData.append("tasa_bcv", dollarPrice);
            formData.append("comprobante", pagoData.comprobante);

            if (metodoPago === "VES") {
                formData.append("banco_emisor", pagoData.bancoEmisorVes);
                formData.append("banco_receptor", BANCO_RECEPTOR);
                formData.append("referencia", pagoData.referenciaVes);

            }

            await axios.post(`${apiUrl}/api/v1/cuotas/pago-cuota`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data"
                },
            });

            toast.success("Pago registrado con éxito");
            onPagoRegistrado(contrato.numero_contrato);
            onClose();
        } catch (error) {
            toast.error("Error al registrar el pago");
        }
    };

    return (
        <Modal open={open} onClose={onClose} closeAfterTransition BackdropComponent={Backdrop}>
            <Box sx={{
                position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                width: "600px", bgcolor: "background.paper", boxShadow: 24, p: 4, borderRadius: 2
            }}>
                <Typography sx={{ fontFamily: "Bakbak One", color: "#0A4575", mb: 2 }} variant="h6">
                    REPORTAR PAGO DE CUOTA #{cuota?.no_cuota}
                </Typography>

                <Box sx={{ mb: 3, p: 2, bgcolor: "#f0f7ff", borderRadius: 1, border: "1px solid #0A4575" }}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography variant="caption">Valor de la Cuota:</Typography>
                            <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "20px" }}>
                                ${parseFloat(cuota?.monto_por_cuota).toFixed(2)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption">Tasa del día:</Typography>
                            <Typography variant="h6" sx={{ fontWeight: "bold" }}>Bs. {dollarPrice}</Typography>
                        </Grid>
                    </Grid>
                </Box>

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        {/* Selector de Método de Pago con restricción para productos */}
                        <Grid item xs={6}>
                            {cuota?.tipo_plan === "producto" ? (
                                <Box sx={{ p: 1, bgcolor: "#f5f5f5", borderRadius: 1, border: "1px solid #ddd" }}>
                                    <Typography variant="body1" sx={{ fontWeight: "bold", color: "#0A4575" }}>
                                        {cuota.metodo_pago}
                                    </Typography>
                                </Box>
                            ) : (
                                <FormControl fullWidth size="small">
                                    <InputLabel>Método de Pago</InputLabel>
                                    <Select
                                        value={metodoPago}
                                        label="Método de Pago"
                                        onChange={(e) => setMetodoPago(e.target.value)}
                                    >
                                        <MenuItem value="USD">USD - Dólares</MenuItem>
                                        <MenuItem value="VES">VES - Bolívares</MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                        </Grid>

                        {/* Monto a Pagar Bloqueado */}
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label={`Monto a Pagar (${metodoPago})`}
                                value={montoPagar}
                                size="small"
                                InputProps={{ readOnly: true }}
                                sx={{ bgcolor: "#f9f9f9" }}
                            />
                        </Grid>

                        {/* Checkbox de Efectivo solo si es USD */}
                        {metodoPago === "USD" && (
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={esPagoEfectivoDivisas}
                                            onChange={(e) => setEsPagoEfectivoDivisas(e.target.checked)}
                                        />
                                    }
                                    label="Pago en Efectivo (Aplica 20% bonificación)"
                                />
                            </Grid>
                        )}

                        {/* Campos de Bolívares */}
                        {metodoPago === "VES" && (
                            <>
                                <Grid item xs={6}>
                                    <FormControl fullWidth size="small" sx={{ boxShadow: 1 }}>
                                        <InputLabel>Banco Emisor</InputLabel>
                                        <Select
                                            value={pagoData.bancoEmisorVes}
                                            onChange={(e) =>
                                                setPagoData({ ...pagoData, bancoEmisorVes: e.target.value })
                                            }
                                            label="Banco Emisor"
                                        >
                                            {banks.map((b) => (
                                                <MenuItem key={b.value} value={b.value}>
                                                    {b.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        sx={{ boxShadow: 1 }}
                                        fullWidth
                                        label="Referencia"
                                        size="small"
                                        value={pagoData.referenciaVes}
                                        onChange={(e) =>
                                            setPagoData({ ...pagoData, referenciaVes: e.target.value })
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            p: 1,
                                            border: "1px dashed #0A4575",
                                            mt: 1,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            bgcolor: "#fffbe6",
                                        }}
                                    >
                                        <Typography sx={{ fontSize: "11px", fontWeight: "bold" }}>
                                            {BANCO_RECEPTOR}: {NUMERO_CUENTA}
                                        </Typography>
                                        <Typography sx={{ fontSize: "11px", fontWeight: "bold" }}>
                                            RIF: {RIF}
                                        </Typography>
                                        <Typography sx={{ fontSize: "11px", fontWeight: "bold" }}>
                                            Pago Móvil: {TELEFONO_PAGO_MOVIL}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12}>
                            <input
                                type="file"
                                hidden
                                id="comprobante"
                                onChange={handleFileChange}
                                accept="image/*,application/pdf"
                            />

                            <label htmlFor="comprobante">
                                <Button
                                    variant="contained"
                                    component="span"
                                    fullWidth
                                    sx={{ mb: 1, bgcolor: "#0A4575" }}
                                >
                                    {pagoData.comprobante
                                        ? "Cambiar Comprobante"
                                        : "Subir Comprobante de Pago"}
                                </Button>
                            </label>

                            {pagoData.comprobante && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: "block",
                                        textAlign: "center",
                                        color: "green",
                                        fontWeight: "bold",
                                    }}
                                >
                                    Archivo: {pagoData.comprobante.name}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>

                    {/* Resumen de Abono */}
                    <Box sx={{ mt: 3, p: 2, bgcolor: "#e8f5e9", borderRadius: 1, border: "1px solid #c8e6c9" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>RESUMEN DE OPERACIÓN:</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography sx={{ fontWeight: 'bold' }}>Monto a aplicar al contrato (USD):</Typography>
                            <Typography sx={{ fontWeight: 'bold', color: '#0A4575' }}>
                                ${((metodoPago === "VES" ? montoPagar / dollarPrice : Number(montoPagar) + descuentoAplicado)).toFixed(2)}
                            </Typography>
                        </Box>
                        {descuentoAplicado > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'green' }}>
                                <Typography>Bonificación por efectivo (USD):</Typography>
                                <Typography sx={{ fontWeight: 'bold' }}>+ ${descuentoAplicado.toFixed(2)}</Typography>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #ccc', mt: 1, pt: 1 }}>
                            <Typography sx={{ fontWeight: 'bold' }}>Total a pagar en {metodoPago}:</Typography>
                            <Typography sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '1.1rem' }}>
                                {metodoPago === "VES" ? (montoPagar * dollarPrice).toFixed(2) : montoPagar} {metodoPago}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                        <Button onClick={onClose} sx={{ mr: 2, color: "#666" }}>Cancelar</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={!isFormValid()}
                            sx={{ bgcolor: "#0A4575" }}
                        >
                            Registrar Pago
                        </Button>
                    </Box>
                </form>
            </Box>
        </Modal>
    );
};

export default ReportePagosCuotasModal;